/**
 * Central axios instance. Always sends cookies for session auth.
 * Auto-refreshes access token on 401 (once), then retries the original request.
 *
 * CSRF: her state-changing request'te `X-CSRF-Token` header'ı otomatik
 * eklenir. Backend'in set ettiği `csrf_token` cookie'sinden okunur.
 * Bu double-submit pattern CSRF saldırılarına karşı savunma sağlar —
 * bir attacker site cookie'ye ulaşamaz, header'ı set edemez.
 */
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CSRF_COOKIE = "csrf_token";
const CSRF_HEADER = "X-CSRF-Token";
const UNSAFE_METHODS = new Set(["post", "put", "patch", "delete"]);

function readCookie(name) {
    if (typeof document === "undefined") return null;
    const match = document.cookie
        .split("; ")
        .find((c) => c.startsWith(`${name}=`));
    return match ? decodeURIComponent(match.split("=")[1]) : null;
}

export const api = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
});

// CSRF: state-changing request'lere header enjekte et
api.interceptors.request.use((config) => {
    const method = (config.method || "get").toLowerCase();
    if (UNSAFE_METHODS.has(method)) {
        const token = readCookie(CSRF_COOKIE);
        if (token) {
            config.headers = config.headers || {};
            config.headers[CSRF_HEADER] = token;
        }
    }
    return config;
});

// Auto-refresh flow: if a request 401s and it's not a retry, hit /auth/refresh once.
let refreshing = null;
api.interceptors.response.use(
    (r) => r,
    async (error) => {
        const { config, response } = error;
        if (!response || response.status !== 401 || config._retry) throw error;
        // Do not attempt refresh for auth endpoints themselves
        if (config.url && config.url.includes("/auth/")) throw error;

        config._retry = true;
        try {
            refreshing =
                refreshing ||
                api.post("/auth/refresh").finally(() => (refreshing = null));
            await refreshing;
        } catch {
            throw error;
        }
        return api(config);
    }
);

// Separate instance for admin scope (uses admin_* cookies via the same domain)
export const adminApi = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
});

// Admin da CSRF header'ı gönderir
adminApi.interceptors.request.use((config) => {
    const method = (config.method || "get").toLowerCase();
    if (UNSAFE_METHODS.has(method)) {
        const token = readCookie(CSRF_COOKIE);
        if (token) {
            config.headers = config.headers || {};
            config.headers[CSRF_HEADER] = token;
        }
    }
    return config;
});

export { API_BASE };
